<template>
  <form class="search-box" role="search" method="post" @submit.prevent="submitSearch">
    <div class="search-field">
      <span class="i-lucide-search search-field-icon" aria-hidden="true" />
      <input
        ref="searchInput"
        type="search" enterkeyhint="search"
        :aria-label="placeholder || t('ux.publicMusicSearchPlaceholder')"
        :value="modelValue" :placeholder="placeholder || t('ux.publicMusicSearchPlaceholder')"
        @input="handleInput" @compositionstart="handleCompositionStart" @compositionend="handleCompositionEnd"
      />
      <button v-if="modelValue" type="button" :aria-label="t('common.clear')"
        class="musicmint-dismiss search-clear" @click="clearSearch">
        <span class="i-lucide-x" aria-hidden="true" />
      </button>
    </div>
    <button type="submit" :aria-label="t('ux.search')" class="search-submit">
      <span class="search-submit-text">{{ t('ux.search') }}</span>
      <span class="i-lucide-search search-submit-icon" aria-hidden="true" />
    </button>
    <button v-if="showCancel && !modelValue" type="button" class="search-cancel" @click="$emit('cancel')">
      {{ t('common.cancel') }}
    </button>
  </form>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { ref } from 'vue';

const { t } = useI18n();
const isComposing = ref(false);
const searchInput = ref<HTMLInputElement | null>(null);
defineExpose({ focusInput: () => searchInput.value?.focus() });

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  showCancel: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'cancel', 'input-complete', 'submit']);
const submitSearch = () => { if (!isComposing.value) emit('submit', props.modelValue.trim()); };
const clearSearch = () => {
  // Search page retains its original clear-first, back-when-empty Cancel path.
  if (props.showCancel) emit('cancel');
  else {
    emit('update:modelValue', '');
    emit('input-complete', '');
  }
};

const handleInput = (event: Event) => {
  const value = (event.target as HTMLInputElement).value;
  emit('update:modelValue', value);
  
  // 只在非输入法编辑状态下触发 input-complete
  if (!isComposing.value) {
    emit('input-complete', value);
  }
};

const handleCompositionStart = () => {
  isComposing.value = true;
};

const handleCompositionEnd = (event: CompositionEvent) => {
  isComposing.value = false;
  // 在输入法编辑结束时触发 input-complete
  emit('input-complete', (event.target as HTMLInputElement).value);
};
</script>

<style scoped>
.search-box { display: flex; width: 100%; align-items: center; gap: 8px; padding-block: 8px; }
.search-field { display: flex; min-width: 0; flex: 1; align-items: center; min-height: 48px; border: 1px solid #ffffff20; border-radius: 12px; background: #1f2937; }
.search-field:focus-within { outline: 2px solid var(--mint-focus, #a5b4fc); outline-offset: 2px; border-color: transparent; }
.search-field-icon { width: 18px; height: 18px; flex: none; margin-inline: 14px 10px; color: #a0a9ba; }
.search-field input { width: 100%; min-width: 0; min-height: 46px; padding: 10px 8px 10px 0; border: 0; border-radius: 12px; background: transparent; color: #f4f5fa; font-size: 16px; outline: none; box-shadow: none; }
.search-field input::placeholder { color: #a0a9ba; }
.search-field input::-webkit-search-cancel-button { -webkit-appearance: none; }
.search-field .search-clear { margin-inline-end: 2px; flex-shrink: 0; color: #a0a9ba; }
.search-submit { display: inline-flex; min-height: 48px; min-width: 48px; align-items: center; justify-content: center; gap: 8px; padding: 10px 18px; border: 1px solid #818cf840; border-radius: 12px; background: #818cf81a; color: #e0e7ff; font-size: 14px; font-weight: 550; }
.search-submit:hover { background: #818cf82a; border-color: #818cf870; }
.search-submit-icon { display: none; }
.search-cancel { flex: none; min-height: 48px; padding-inline: 10px; color: #b0b6c9; font-size: 14px; }
.search-cancel:hover { color: #f4f5fa; }
.search-box button:not(.musicmint-dismiss):focus-visible { outline: 2px solid var(--mint-focus, #a5b4fc); outline-offset: 2px; }
@media (max-width: 639px) {
  .search-submit { padding: 12px; }
  .search-submit-text { display: none; }
  .search-submit-icon { display: inline-block; width: 20px; height: 20px; }
}
</style>
